import {
  Accordion,
  BasicCollapsible,
  Column,
  Divider,
  FlexBox,
  getTokenCssVar,
  GridContainer,
  H3,
  Li,
  Link,
  Row,
  Ul,
  Visible,
  type ColumnProps,
} from '@vp/swan'
import type { AwardCallout, FullFooterLinkList, FullFooterSimpleLink } from '../../../types/site-footer'
import { AwardCalloutSection } from './AwardCalloutSection'
import { FOOTER_NAVIGATION } from '../../../constants/analytics'
import { useAuth } from '../../../hooks/useAuth'
import { footerNavigationLink as footerNavigationLinkClass } from './FooterNavigation.scss'

interface FooterNavigationProps {
  footerNavigation: FullFooterLinkList[]
  awardCallout?: AwardCallout
  isLoggedInSsr?: boolean
}

const FIND_TRACK_ORDER_ANALYTICS_ID = 'orderHistory'
const FIND_TRACK_ORDER_GUEST_PATH = '/od'

const toGuestUrl = (signedInUrl: string): string => {
  try {
    return new URL(FIND_TRACK_ORDER_GUEST_PATH, signedInUrl).toString()
  } catch {
    return signedInUrl
  }
}
const resolveHref = (link: FullFooterSimpleLink, isSignedIn: boolean): string =>
  link.analyticsId === FIND_TRACK_ORDER_ANALYTICS_ID && !isSignedIn ? toGuestUrl(link.url) : link.url

const NavLinkList = ({ links, label, isSignedIn }: { links?: FullFooterSimpleLink[]; label?: string; isSignedIn: boolean }) => {
  const getTrackingData = (link: FullFooterSimpleLink, href: string) => {
    return JSON.stringify({
      bookendName: FOOTER_NAVIGATION,
      navigationDetailPath: link.analyticsId ? [link.analyticsId] : [],
      href,
    })
  }

  return (
    <Ul role="list" aria-label={label} skin="minimal">
      {links?.map((link: FullFooterSimpleLink, index: number) => {
        const href = resolveHref(link, isSignedIn)
        return (
          <Li fontSkin="body-small" key={`${link.url}-${index}`}>
            <Link href={href} skin="unstyled" data-tracking={getTrackingData(link, href)} className={footerNavigationLinkClass}>
              {link.text}
            </Link>
          </Li>
        )
      })}
    </Ul>
  )
}

export const FooterNavigation: React.FC<FooterNavigationProps> = ({ footerNavigation, awardCallout, isLoggedInSsr = false }) => {
  const { isSignedIn: isSignedInClient } = useAuth()
  // SSR renders with the server-known auth flag; the client reconciles once identity resolves (no href flicker).
  const isSignedIn = isSignedInClient ?? isLoggedInSsr

  if (!footerNavigation?.length) return null

  const spanLength = Math.min(Math.ceil(12 / (footerNavigation.length - 1)), 4) as ColumnProps['span']

  return (
    <GridContainer>
      <FlexBox as={Row} style={{ columnGap: getTokenCssVar('SwanSemSpace4') }} ml={0} justifyContent="space-between" flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
        {footerNavigation.map((navSection, index) => (
          <Column key={navSection.listHeader ?? `footer-nav-${index}`} span={spanLength} spanXs={12} spanSm={12} pl={0}>
            <Visible md lg xl>
              <H3 fontSkin="body-small-bold">{navSection.listHeader}</H3>
              <NavLinkList links={navSection.links} label={navSection.listHeader} isSignedIn={isSignedIn} />
            </Visible>

            <Visible xs sm>
              <Divider />
              <Accordion>
                <BasicCollapsible
                  collapsibleId={`footer-${index}-${navSection.listHeader ?? navSection.text}`}
                  heading={navSection.listHeader}
                  headingLevel="h3"
                >
                  <NavLinkList links={navSection.links} label={navSection.listHeader} isSignedIn={isSignedIn} />
                </BasicCollapsible>
              </Accordion>
            </Visible>
          </Column>
        ))}

        {awardCallout && <AwardCalloutSection awardCallout={awardCallout} spanLength={spanLength} />}
      </FlexBox>
    </GridContainer>
  )
}
